import type { SettingValue } from '@sentra/contracts';
import { SETTINGS_REGISTRY } from '@sentra/contracts';
import {
  createContext,
  type ReactNode,
  use,
  useCallback,
  useEffect,
  useState,
  useSyncExternalStore,
} from 'react';

/**
 * Light and dark, driven by `ui.default_theme` and by the operator's own system preference.
 *
 * **Three values, not two.** `system` is a real choice rather than the absence of one: a terminal
 * at a gate that follows the building's daylight schedule and a laptop that is always dark are two
 * different requirements, and a boolean cannot express the first. The three come from the settings
 * registry in `@sentra/contracts`, so the browser and the administration screen that writes the
 * setting are talking about the same set.
 *
 * **The resolved theme is a CLASS on the document element, never the media query alone.** The token
 * sheet declares its dark palette under `.dark`, so `system` is implemented by writing that class
 * from the media query rather than by letting the media query style the document — which is what
 * makes an explicit `light` on a machine set to dark actually light.
 *
 * **The class is written in a layout effect's place, before paint, by the bootstrap.** A theme
 * applied in an ordinary effect paints light first and then repaints dark, which is the flash every
 * operator notices and nobody can describe. `applyTheme` is exported for that reason and is called
 * once before the tree is mounted.
 */

/** `light`, `dark` or `system`, the three values `ui.default_theme` admits. */
export type ThemePreference = SettingValue<'ui.default_theme'>;

/** What a preference resolves to once the system has been asked. */
export type ResolvedTheme = 'light' | 'dark';

/** The installation default, read from the one registry that declares it. */
export const DEFAULT_THEME_PREFERENCE: ThemePreference =
  SETTINGS_REGISTRY['ui.default_theme'].defaultValue;

/** Where this terminal's own choice is remembered. */
export const THEME_STORAGE_KEY = 'sentra.theme';

/** The query the `system` preference is resolved through. */
const DARK_SCHEME_QUERY = '(prefers-color-scheme: dark)';

const PREFERENCES: readonly ThemePreference[] = ['system', 'light', 'dark'];

function isThemePreference(value: string): value is ThemePreference {
  return (PREFERENCES as readonly string[]).includes(value);
}

/** The preference remembered on this terminal, or `null`. A refusing storage means `null`. */
export function storedThemePreference(storage: Pick<Storage, 'getItem'>): ThemePreference | null {
  let raw: string | null;
  try {
    raw = storage.getItem(THEME_STORAGE_KEY);
  } catch {
    return null;
  }
  return raw !== null && isThemePreference(raw) ? raw : null;
}

/** Asks the platform what the operator's system preference is. */
function systemTheme(): ResolvedTheme {
  return globalThis.matchMedia(DARK_SCHEME_QUERY).matches ? 'dark' : 'light';
}

/** What a preference means right now. */
export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  return preference === 'system' ? systemTheme() : preference;
}

/**
 * Whether a media query matches, as an external store rather than as state.
 *
 * `useSyncExternalStore` is the right shape for this and not merely a tidier one: the platform owns
 * the value, so reading it in an effect and copying it into state is a render, then a second render
 * to correct the first — and the copy is stale for exactly one paint. Subscribing reads the live
 * answer at render time and re-renders only when the platform says it changed.
 */
function useMediaQuery(query: string): boolean {
  // **Both arguments are memoised on the query, and that is not a micro-optimisation.** React
  // re-subscribes whenever `subscribe` is a different function, so an inline one tears the
  // `change` listener down and re-establishes it on every render of this provider — and
  // `getSnapshot` constructed a fresh `MediaQueryList` each time it was called. Neither is a leak,
  // since the cleanup matches the object it registered on, but it is the documented pitfall and it
  // opens a window, however small, in which a change can land between the two.
  const subscribe = useCallback(
    (onChange: () => void) => {
      const media = globalThis.matchMedia(query);
      media.addEventListener('change', onChange);
      return () => {
        media.removeEventListener('change', onChange);
      };
    },
    [query],
  );
  const snapshot = useCallback(() => globalThis.matchMedia(query).matches, [query]);
  return useSyncExternalStore(subscribe, snapshot);
}

/**
 * Writes the resolved theme onto the document element.
 *
 * Exported so the bootstrap can call it before the first paint. Toggling a class rather than
 * replacing `className` leaves anything else on that element alone, which matters because the
 * element is shared with whatever a browser extension has put there.
 */
export function applyTheme(theme: ResolvedTheme, element: HTMLElement): void {
  element.classList.toggle('dark', theme === 'dark');
}

interface ThemeContextValue {
  readonly preference: ThemePreference;
  readonly resolved: ResolvedTheme;
  readonly setPreference: (next: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/** What a preference means, given what the platform currently says. */
function resolvePreference(preference: ThemePreference, systemPrefersDark: boolean): ResolvedTheme {
  if (preference !== 'system') {
    return preference;
  }
  return systemPrefersDark ? 'dark' : 'light';
}

export function ThemeProvider({
  initialPreference,
  children,
}: Readonly<{
  initialPreference: ThemePreference;
  children: ReactNode;
}>): ReactNode {
  const [preference, setStoredPreference] = useState<ThemePreference>(initialPreference);
  const systemPrefersDark = useMediaQuery(DARK_SCHEME_QUERY);
  // Derived, not stored. An explicit `light` or `dark` ignores the platform entirely, which is what
  // makes an operator's own choice survive the building's schedule flipping at sunset.
  const resolved = resolvePreference(preference, systemPrefersDark);

  useEffect(() => {
    applyTheme(resolved, document.documentElement);
  }, [resolved]);

  const setPreference = useCallback((next: ThemePreference) => {
    setStoredPreference(next);
    try {
      globalThis.localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // A preference that cannot be persisted is still a preference for this shift.
    }
  }, []);

  return <ThemeContext value={{ preference, resolved, setPreference }}>{children}</ThemeContext>;
}

/** The query an operator's request for less movement arrives as. */
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * Whether this operator has asked their system for less movement.
 *
 * Read here rather than through the animation library's own hook, which resolves the query once
 * when its module is first imported. That is fine in a browser and wrong in a test, where the
 * preference is stated after the import — and a preference that cannot be exercised is a preference
 * nothing proves is honoured.
 */
export function usePrefersReducedMotion(): boolean {
  return useMediaQuery(REDUCED_MOTION_QUERY);
}

/** The active theme and the one way to change it. */
export function useTheme(): ThemeContextValue {
  const value = use(ThemeContext);
  if (value === null) {
    throw new Error('useTheme was called outside ThemeProvider');
  }
  return value;
}
